import React, { useState, useEffect } from '@wordpress/element';
import { renderInputRow } from '../../../utils/renderInputRow';
import apiFetch from '@wordpress/api-fetch';

const GeneralSettingsTab = () => {
  const [originalSettings, setOriginalSettings] = useState({});
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null

  // --- FUNCTION TO GET SETTINGS DATA --- //
  useEffect(() => {
    const global = window.CFAFWR_GLOBAL_OPTIONS || {};
    setSettings(global);
    setOriginalSettings(global);
  }, []);

  // --- SAVE GENERAL SETTINGS --- //
  const handleSave = () => {
    const changedData = Object.fromEntries(
      Object.entries(settings).filter(([key, value]) => {
        return originalSettings[key] !== value;
      })
    );
    if (Object.keys(changedData).length === 0) {
      setSaveStatus('nochange');
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }
    setLoading(true);
    apiFetch({
      path: '/wp/v2/settings',
      method: 'POST',
      data: changedData,
    })
    .then(() => {
      setLoading(false);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3500);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      window.CFAFWR_GLOBAL_OPTIONS = {
        ...(window.CFAFWR_GLOBAL_OPTIONS || {}),
        ...settings,
      };
      setOriginalSettings(settings);
    })
    .catch((err) => {
      setLoading(false);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 4000);
      console.error('Error saving general settings:', err);
    });
  };

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <>
      {/* LOADER */}
      {loading && (
        <div className="cpiw-overlay-loader">
          <div className="cpiw-spinner"></div>
        </div>
      )}

      <div className="cf-card">
        <div className="cf-card__header">
          <div className="cf-card__header-left">
            <div className="cf-card__header-icon">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </div>
            <div>
              <h2 className="cf-card__title">General Settings</h2>
              <p className="cf-card__description">Configure plugin behaviour and display preferences</p>
            </div>
          </div>
        </div>

        <div className="cf-card__body">
          <div className="cf-form">

            {renderInputRow('Enable Authentication', 'cfafwr_enable_plugin', 'checkBox', [], '', settings, updateSetting)}

            {renderInputRow('Enable User Registration Email', 'cfafwr_user_email_sent', 'checkBox', [], '', settings, updateSetting)}

            {renderInputRow(
              'Registration Email Subject',
              'cfafwr_user_email_subject_msg',
              '',
              [],
              '',
              settings,
              updateSetting,
              '',
              true
            )}

            {renderInputRow(
              'Registration Email Body',
              'cfafwr_user_email_body_msg',
              'textArea',
              [],
              '',
              settings,
              updateSetting,
              <><b>Note:</b> <code>{'{site_name}'}</code> = {typeof CFAFWR_DATA !== 'undefined' ? CFAFWR_DATA.siteName : '{site_name}'}</>,
              true
            )}

            {renderInputRow('Hide Field Labels', 'cfafwr_hide_field_labels', 'select', [
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ], '', settings, updateSetting)}

            {renderInputRow('Change Login/Register Title Text', 'cfafwr_login_reg_change_text', 'checkBox', [], 'Enable/Disable', settings, updateSetting)}

            {settings.cfafwr_login_reg_change_text === 'yes' && (
              <>
                {renderInputRow('Login Title Text', 'cfafwr_login_change_text', '', [], '', settings, updateSetting)}
                {renderInputRow('Register Title Text', 'cfafwr_reg_change_text', '', [], '', settings, updateSetting)}
              </>
            )}

            {renderInputRow(
              'Required Field Message',
              'cfafwr_field_label_require_text',
              '',
              [],
              '',
              settings,
              updateSetting,
              <><b>Note:</b> <code>{'{field_label}'}</code> = the field label name</>
            )}

            {renderInputRow('My Account Tab Title', 'cfafwr_myac_tab_title', '', [], '', settings, updateSetting)}

            {renderInputRow('My Account Tab Form Heading', 'cfafwr_myac_tab_form_head', '', [], '', settings, updateSetting)}

            {renderInputRow('Show Custom Field Position', 'cfafwr_show_field_register', 'select', [
              { label: 'Register Form Start', value: 'register_form_start' },
              { label: 'Before Register Button', value: 'before_register_form' },
            ], '', settings, updateSetting)}

          </div>

          {/* Form Footer */}
          <div className="cf-form-footer">
            {saveStatus === 'success' && (
              <span style={{ fontSize: 13, color: '#22c08b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                Settings saved successfully!
              </span>
            )}
            {saveStatus === 'error' && (
              <span style={{ fontSize: 13, color: '#f04c5a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                Error saving settings. Please try again.
              </span>
            )}
            {saveStatus === 'nochange' && (
              <span style={{ fontSize: 13, color: '#8c96b5', fontWeight: 500 }}>
                No changes to save.
              </span>
            )}
            <button
              type="button"
              className="cf-btn cf-btn--primary cf-btn--lg"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? (
                <>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{animation:'cf-spin 0.7s linear infinite'}}>
                    <line x1="12" y1="2" x2="12" y2="6"/>
                    <line x1="12" y1="18" x2="12" y2="22"/>
                    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/>
                    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
                    <line x1="2" y1="12" x2="6" y2="12"/>
                    <line x1="18" y1="12" x2="22" y2="12"/>
                    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/>
                    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
                  </svg>
                  Saving…
                </>
              ) : (
                <>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                    <polyline points="17 21 17 13 7 13 7 21"/>
                    <polyline points="7 3 7 8 15 8"/>
                  </svg>
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default GeneralSettingsTab;
